/**
 * Document Viewer
 *
 * Shell-agnostic document renderer with citation highlighting. Wrapped by
 * both DocumentPreviewModal (full-screen Dialog) and ChatDocumentPanel
 * (docked side pane). Supports PDF (bounding boxes), DOCX (docx-preview),
 * Excel/TXT/CSV (HTML), images, and a chunk text fallback.
 *
 * Owns: zoom, page-render/highlight effects, the pdfjs worker config, and
 * arrow/zoom keyboard handling. Does NOT own Esc-to-close — the wrapping
 * shell decides that (Radix for the modal, an explicit listener for the pane).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { Button, buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { PdfHighlightOverlay } from '@/components/ui/pdf-highlight-overlay'
import { DocxViewer } from '@/components/ui/docx-viewer'
import { HtmlDocumentViewer } from '@/components/ui/html-document-viewer'
import { ChunkTextViewer } from '@/components/ui/chunk-text-viewer'
import { FileTypeIcon } from '@/components/ui/file-type-icon'
import {
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Loader2,
  X,
  ZoomIn,
  ZoomOut,
  AlertTriangle,
} from 'lucide-react'
import { usePreviewStore } from '@/stores/preview-store'
import { highlightTextInDOM, clearHighlightsInDOM, findLongestQuoteSegment } from '@/lib/text-highlight'
import { scrollHighlightIntoView, topmostElement } from '@/lib/scroll-highlight'
import { orderCandidatePages, pickQuotePage } from '@/lib/citation-page-resolution'

// Configure pdf.js worker
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString()

// ===================
// Constants
// ===================

const ZOOM_STEP = 0.25
const MIN_ZOOM = 0.5
const MAX_ZOOM = 3
const DEFAULT_ZOOM = 1

const VIEWABLE_IMAGE_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
])

const DOCX_MIME_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
])

/** Milliseconds to wait for react-pdf to paint the text layer after the canvas */
const TEXT_LAYER_SETTLE_MS = 300

// ===================
// Types
// ===================

/**
 * The slice of pdf.js's PDFDocumentProxy this viewer needs. Kept structural
 * (and deliberately loose about text items — pdf.js mixes TextItem with
 * marked-content markers that carry no `str`) so it stays assignable from the
 * real proxy without importing pdf.js's types.
 */
interface PdfDocumentProxy {
  numPages: number
  getPage: (pageNumber: number) => Promise<{
    getTextContent: () => Promise<{ items: ReadonlyArray<object> }>
  }>
}

/** pdf.js text items carry `str`; marked-content markers don't. */
function textItemString(item: object): string {
  return 'str' in item && typeof item.str === 'string' ? item.str : ''
}

// ===================
// Component
// ===================

export function DocumentViewer({ onClose }: { onClose: () => void }) {
  const {
    isOpen,
    documentName,
    mimeType,
    size,
    url,
    page,
    isLoading,
    error,
    citationLabel,
    highlights,
    activeHighlightId,
    textHighlight,
    htmlUrl,
    originalDownloadUrl,
    originalFileName,
    conversionStatus,
    setPage,
    downloadDocument,
  } = usePreviewStore()

  const [numPages, setNumPages] = useState<number | null>(null)
  const [zoom, setZoom] = useState(DEFAULT_ZOOM)
  const [pdfError, setPdfError] = useState<string | null>(null)
  const [pageViewport, setPageViewport] = useState<{
    convertToViewportRectangle: (rect: number[]) => number[]
    width: number
    height: number
  } | null>(null)
  const pageWrapperRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // The loaded pdf.js document proxy — used to read neighbouring pages' text
  // when the cited page turns out not to contain the quote (F13).
  const pdfProxyRef = useRef<PdfDocumentProxy | null>(null)
  /** Guards the page-correction pass so it runs once per citation, never in a loop */
  const pageCorrectionKeyRef = useRef<string | null>(null)

  // Quote-first suppression: while a text-layer quote search is pending (or has
  // succeeded), the coarse chunk-rect overlay stays hidden; it renders only
  // when the search misses (scanned/rotated PDFs) or no quote exists.
  const [quoteSearch, setQuoteSearch] = useState<'idle' | 'pending' | 'matched' | 'unmatched'>('idle')

  const currentPage = page && page >= 1 ? page : 1
  const isPdf = mimeType === 'application/pdf'
  const isImage = mimeType ? VIEWABLE_IMAGE_TYPES.has(mimeType) : false
  const isDocx = mimeType ? DOCX_MIME_TYPES.has(mimeType) : false
  const hasHtmlPreview = !!htmlUrl
  const isViewable = isPdf || isImage || isDocx || hasHtmlPreview

  // Does this viewer support zoom controls?
  const supportsZoom = isPdf || isDocx || hasHtmlPreview

  // Reset state when document changes
  useEffect(() => {
    if (isOpen) {
      setZoom(DEFAULT_ZOOM)
      setPdfError(null)
      setNumPages(null)
      setPageViewport(null)
      pageCorrectionKeyRef.current = null
    }
  }, [isOpen, url])

  // A new citation re-arms the page correction. The guard exists only to stop a
  // correction chaining *within* one citation — carrying it across clicks meant
  // re-opening an already-corrected citation left the reader stranded on the
  // cited page with nothing highlighted. Keyed on the highlight id and on the
  // textHighlight object, which the store recreates on every citation click.
  useEffect(() => {
    pageCorrectionKeyRef.current = null
  }, [activeHighlightId, textHighlight])

  // Reset viewport when page or zoom changes
  useEffect(() => {
    setPageViewport(null)
  }, [currentPage, zoom])

  // Keyboard navigation (arrows + zoom). Esc is owned by the wrapping shell.
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return

      switch (e.key) {
        case 'ArrowLeft':
          if (isPdf) {
            e.preventDefault()
            if (currentPage > 1) setPage(currentPage - 1)
          }
          break
        case 'ArrowRight':
          if (isPdf) {
            e.preventDefault()
            if (numPages && currentPage < numPages) setPage(currentPage + 1)
          }
          break
        case '=':
        case '+':
          if (supportsZoom) {
            e.preventDefault()
            setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP))
          }
          break
        case '-':
          if (supportsZoom) {
            e.preventDefault()
            setZoom((z) => Math.max(MIN_ZOOM, z - ZOOM_STEP))
          }
          break
        case '0':
          if (supportsZoom) {
            e.preventDefault()
            setZoom(DEFAULT_ZOOM)
          }
          break
      }
    },
    [isOpen, isPdf, supportsZoom, currentPage, numPages, setPage]
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  function onDocumentLoadSuccess(pdf: PdfDocumentProxy) {
    pdfProxyRef.current = pdf
    setNumPages(pdf.numPages)
    setPdfError(null)
  }

  function onDocumentLoadError(error: Error) {
    setPdfError(error.message || 'Failed to load PDF')
  }

  // Capture viewport when page renders for highlight positioning
  function onPageRenderSuccess(page: { getViewport: (options: { scale: number }) => { convertToViewportRectangle: (rect: number[]) => number[], width: number, height: number } }) {
    const viewport = page.getViewport({ scale: zoom })
    setPageViewport(viewport)
  }

  // Apply text-search highlighting to the PDF text layer after page renders
  useEffect(() => {
    if (!isPdf || !pageViewport || !textHighlight?.chunkText || !pageWrapperRef.current) {
      // A citation without a quote must not inherit the previous quote's marks
      const staleLayer = pageWrapperRef.current?.querySelector('.textLayer') as HTMLElement | null
      if (staleLayer) clearHighlightsInDOM(staleLayer)
      setQuoteSearch('idle')
      return undefined
    }

    setQuoteSearch('pending')

    // Wait for the text layer to render after the page canvas
    const timeoutId = setTimeout(() => {
      const textLayerEl = pageWrapperRef.current?.querySelector('.textLayer') as HTMLElement | null
      if (!textLayerEl) {
        setQuoteSearch('unmatched')
        return
      }

      clearHighlightsInDOM(textLayerEl)

      // Try full quote first. scrollIntoView is left off throughout: this
      // component owns the scroll so the passage lands in the upper third of
      // the viewer's own scroll container rather than wherever the browser's
      // scrollIntoView drags every scrollable ancestor.
      highlightTextInDOM(textLayerEl, textHighlight.chunkText, textHighlight.sectionHeader, false)

      // If full quote didn't match (PDF tables reorder text), try individual sentences
      if (!textLayerEl.querySelector('mark')) {
        const sentences = textHighlight.chunkText
          .split(/(?<=[.!?])\s+/)
          .filter((s) => s.length > 30)
          .sort((a, b) => b.length - a.length)
        for (const sentence of sentences) {
          highlightTextInDOM(textLayerEl, sentence, null, false)
          if (textLayerEl.querySelector('mark')) break
        }
      }

      // Third tier: tables/bullets — pdf.js reorders the text layer so neither
      // the full quote nor whole sentences match; mark the longest verbatim
      // run of quote words that does appear
      if (!textLayerEl.querySelector('mark')) {
        const segment = findLongestQuoteSegment(textLayerEl, textHighlight.chunkText)
        if (segment) highlightTextInDOM(textLayerEl, segment, null, false)
      }

      const marks = textLayerEl.querySelectorAll<HTMLElement>('mark')
      if (marks.length > 0) {
        setQuoteSearch('matched')
        // F14: opening the page is not enough — put the sentence on screen.
        scrollHighlightIntoView(scrollContainerRef.current, topmostElement(marks))
      } else {
        setQuoteSearch('unmatched')
      }
    }, TEXT_LAYER_SETTLE_MS)
    return () => clearTimeout(timeoutId)
  }, [isPdf, pageViewport, textHighlight])

  // F13: the cited page can be the chunk's first page rather than the page the
  // quoted sentence sits on (chunks span page breaks). When the quote is not on
  // the page we opened, look at the ACTIVE highlight's pages and the immediate
  // neighbours, and move to whichever actually carries it. Runs at most once
  // per citation open — a miss leaves the user on the cited page.
  useEffect(() => {
    if (!isPdf || quoteSearch !== 'unmatched') return
    const quote = textHighlight?.chunkText
    const pdf = pdfProxyRef.current
    if (!quote || !pdf || !numPages) return

    // Keyed on the citation, not the page, so a correction can never chain
    const correctionKey = `${url ?? ''}::${quote}`
    if (pageCorrectionKeyRef.current === correctionKey) return
    pageCorrectionKeyRef.current = correctionKey

    // Only the citation the user just clicked. The store accumulates highlights
    // across clicks, so passing them all would offer up pages from unrelated
    // earlier citations as candidates.
    const candidates = orderCandidatePages({
      citedPage: currentPage,
      highlightPages: highlights
        .filter((h) => h.id === activeHighlightId)
        .flatMap((h) => h.pages.map((p) => p.pageNumber)),
      numPages,
    })
    if (candidates.length === 0) return

    let cancelled = false
    let completed = false

    void (async () => {
      const texts: Array<{ pageNumber: number; text: string }> = []
      for (const pageNumber of candidates) {
        if (cancelled) return
        try {
          const pdfPage = await pdf.getPage(pageNumber)
          const content = await pdfPage.getTextContent()
          texts.push({
            pageNumber,
            text: content.items.map(textItemString).join(' '),
          })
        } catch {
          // A page that won't yield text simply isn't a candidate
        }
      }

      if (cancelled) return
      const resolved = pickQuotePage(texts, quote, currentPage)
      completed = true
      if (resolved != null && resolved !== currentPage) setPage(resolved)
    })()

    return () => {
      cancelled = true
      // An interrupted pass proved nothing — let a later render try again
      if (!completed) pageCorrectionKeyRef.current = null
    }
  }, [
    isPdf,
    quoteSearch,
    textHighlight,
    numPages,
    currentPage,
    url,
    highlights,
    activeHighlightId,
    setPage,
  ])

  // Get highlights for the current page
  const currentPageHighlights = highlights
    .filter((h) => h.pages.some((p) => p.pageNumber === currentPage))
    .map((h) => ({
      ...h,
      rects: h.pages.find((p) => p.pageNumber === currentPage)?.rects ?? [],
    }))

  // Chunk-rect overlays are the fallback for scanned/rotated PDFs where the
  // text-layer quote search can't run.
  const showsRectFallback =
    !!pageViewport && (!textHighlight?.chunkText || quoteSearch === 'unmatched')
  const rectFallbackKey = showsRectFallback
    ? currentPageHighlights.map((h) => `${h.id}:${h.rects.length}`).join('|')
    : ''

  // F14, fallback path: the rect overlays get the same scroll treatment as a
  // matched quote — otherwise a citation on the lower half of the page opens
  // with every highlight below the fold.
  useEffect(() => {
    if (!showsRectFallback || !rectFallbackKey) return undefined

    const rafId = requestAnimationFrame(() => {
      const boxes = pageWrapperRef.current?.querySelectorAll<HTMLElement>(
        '[data-pdf-highlight-rect]'
      )
      if (boxes && boxes.length > 0) {
        scrollHighlightIntoView(scrollContainerRef.current, topmostElement(boxes))
      }
    })
    return () => cancelAnimationFrame(rafId)
  }, [showsRectFallback, rectFallbackKey, currentPage, zoom])

  function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-2 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <FileTypeIcon mimeType={mimeType} fileName={documentName} size={20} className="shrink-0" />
          <div className="min-w-0">
            {/* F28 — this header is where a truncated row name is recovered,
                so it must carry the full string even when it truncates too. */}
            <h2 className="text-sm font-semibold truncate" title={documentName ?? undefined}>
              {documentName ?? 'Document'}
            </h2>
            <div className="flex items-center gap-2 text-meta">
              {size != null && <span>{formatFileSize(size)}</span>}
              {isPdf && numPages != null && <span>{numPages} pages</span>}
              {citationLabel && (
                <span className="text-primary">{citationLabel}</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1">
          {/* Zoom controls (PDF, DOCX, HTML preview) */}
          {supportsZoom && url && !pdfError && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setZoom((z) => Math.max(MIN_ZOOM, z - ZOOM_STEP))}
                disabled={zoom <= MIN_ZOOM}
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-meta w-12 text-center">
                {Math.round(zoom * 100)}%
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP))}
                disabled={zoom >= MAX_ZOOM}
              >
                <ZoomIn className="h-4 w-4" />
              </Button>

              <div className="w-px h-4 bg-border mx-1" />
            </>
          )}

          {/* Page navigation (PDF only) */}
          {isPdf && numPages != null && numPages > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setPage(currentPage - 1)}
                disabled={currentPage <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-meta w-16 text-center">
                {currentPage} / {numPages}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setPage(currentPage + 1)}
                disabled={currentPage >= numPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>

              <div className="w-px h-4 bg-border mx-1" />
            </>
          )}

          <Button variant="ghost" size="icon" onClick={() => void downloadDocument()}>
            <Download className="h-4 w-4" />
          </Button>
          {originalDownloadUrl && originalFileName && (
            <a
              href={originalDownloadUrl}
              download={originalFileName}
              title={`Download the original file (${originalFileName})`}
              className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'h-8 ml-1')}
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              {`Download .${originalFileName.split('.').pop()?.toLowerCase() ?? 'original'}`}
            </a>
          )}
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-auto flex flex-col items-stretch bg-muted/30 p-4"
      >
        {/* PDF conversion failed: original-format viewer still works, but
            page jumps won't (no canonical PDF). Show a subtle notice. */}
        {!isLoading && !error && conversionStatus === 'failed' && (
          <div className="mb-3 rounded-md border border-[hsl(var(--status-warning)/0.3)] bg-[hsl(var(--status-warning)/0.08)] px-3 py-2 text-meta">
            <AlertTriangle className="h-3.5 w-3.5 inline-block mr-1.5 align-text-bottom text-[hsl(var(--status-warning))]" />
            PDF preview unavailable — showing original format. Page-number citations may not jump precisely.
          </div>
        )}

        <div className="flex-1 flex items-start justify-center">
        {isLoading && (
          <div className="flex flex-col items-center justify-center h-full gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading document...</p>
          </div>
        )}

        {/* PDF still being converted from DOCX/XLSX/etc. The viewer can't
            render anything useful yet — block on conversion completing. */}
        {!isLoading && !error && (conversionStatus === 'pending' || conversionStatus === 'converting') && (
          <div className="flex flex-col items-center justify-center h-full gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Converting document for preview…</p>
            <p className="text-meta">This usually takes a few seconds. Try again shortly.</p>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-destructive" />
            </div>
            <p className="text-sm text-destructive">{error}</p>
            <Button variant="ghost" onClick={() => void downloadDocument()}>
              <Download className="h-4 w-4 mr-2" />
              Download instead
            </Button>
          </div>
        )}

        {/* PDF Viewer */}
        {!isLoading && !error && url && isPdf && (
          <Document
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Loading PDF...</span>
              </div>
            }
            error={
              <div className="flex flex-col items-center gap-3">
                <AlertTriangle className="h-6 w-6 text-destructive" />
                <p className="text-sm text-destructive">
                  {pdfError || 'Failed to load PDF'}
                </p>
                <Button variant="ghost" onClick={() => void downloadDocument()}>
                  <Download className="h-4 w-4 mr-2" />
                  Download instead
                </Button>
              </div>
            }
          >
            {/* Page wrapper with position: relative for highlight positioning */}
            <div
              ref={pageWrapperRef}
              className="relative shadow-lg"
              style={pageViewport ? { width: pageViewport.width, height: pageViewport.height } : undefined}
            >
              <Page
                pageNumber={currentPage}
                scale={zoom}
                onRenderSuccess={onPageRenderSuccess}
                loading={
                  <div className="h-[800px] w-[600px] bg-muted animate-pulse rounded" />
                }
              />

              {/* Chunk-rect overlays — fallback only: hidden while a quote
                  search is pending and when the quote matched (quote-first) */}
              {pageViewport &&
                showsRectFallback &&
                currentPageHighlights.map((highlight) => (
                  <PdfHighlightOverlay
                    key={highlight.id}
                    rects={highlight.rects}
                    viewport={pageViewport}
                    pageHeight={pageViewport.height}
                    highlightId={highlight.id}
                    isActive={highlight.id === activeHighlightId}
                  />
                ))}
            </div>
          </Document>
        )}

        {/* Image Viewer */}
        {!isLoading && !error && url && isImage && (
          <img
            src={url}
            alt={documentName ?? 'Document'}
            className="max-w-full max-h-full object-contain shadow-lg rounded"
            style={{ transform: `scale(${zoom})`, transformOrigin: 'top center' }}
          />
        )}

        {/* DOCX Viewer — rendered client-side via docx-preview */}
        {!isLoading && !error && url && isDocx && (
          <DocxViewer
            fileUrl={url}
            textHighlight={textHighlight}
            zoom={zoom}
          />
        )}

        {/* HTML Document Viewer — for Excel, TXT, CSV with server-generated HTML */}
        {!isLoading && !error && !isPdf && !isImage && !isDocx && htmlUrl && (
          <HtmlDocumentViewer
            htmlUrl={htmlUrl}
            textHighlight={textHighlight}
            zoom={zoom}
          />
        )}

        {/* Chunk Text Fallback — when textHighlight exists but no rich preview available */}
        {!isLoading && !error && url && !isViewable && textHighlight && (
          <ChunkTextViewer
            documentName={documentName ?? 'Document'}
            chunkText={textHighlight.chunkText}
            sectionHeader={textHighlight.sectionHeader}
            onDownload={() => void downloadDocument()}
          />
        )}

        {/* Unsupported Format — download only */}
        {!isLoading && !error && url && !isViewable && !textHighlight && (
          <div className="flex flex-col items-center justify-center h-full gap-4">
            <FileText className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
            <div className="text-center">
              <h3 className="text-base font-medium mb-1">Preview unavailable</h3>
              <p className="text-sm text-muted-foreground mb-1">
                {documentName ?? 'This file'} cannot be previewed in the browser.
              </p>
              {size != null && (
                <p className="text-meta">
                  {formatFileSize(size)} &middot; {mimeType}
                </p>
              )}
            </div>
            <Button onClick={() => void downloadDocument()}>
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        )}
        </div>
      </div>
    </div>
  )
}
